import { useRef, useEffect, useLayoutEffect, useMemo } from 'react'
import LoadingMessage from '../LoadingMessage'
import { Message } from '@/service/api'
import MessageBubble from './MessageBubble'
import { getMentionedAgent } from '@/constants/agentNames'
import type { VersionInfo } from '@/features/chatV2/types'
import {
  ToolProgressStatus,
  type ToolProgressData,
} from '@/features/chatV2/components/messages/ToolProgressStatus'

type MessagesProps = {
  messages: Message[]
  conversationId: string | null
  conversationLoading: boolean
  handleFeedbackSubmitted: (messageId: string, rating: number | undefined) => void
  onEditClick?: (message: Message) => void
  isEditDisabled?: boolean
  editingMessageId?: string | null
  isEditSaving?: boolean
  messagesAfterCount?: number
  onEditConfirm?: (newContent: string) => void
  onEditCancel?: () => void
  onRegenerate?: (assistantMessageId: string) => void
  isRegenerateDisabled?: boolean
  // Version navigation props
  getVersionInfo?: (message: Message) => VersionInfo | null
  getUserVersionInfo?: (message: Message) => VersionInfo | null
  onNavigateVersion?: (userMsgId: string, direction: 'prev' | 'next') => void
  // W16 / TIME-198: ephemeral tool-progress status. Rendered in the
  // scroll container directly under the active assistant turn so the
  // line is visually anchored to the bubble it describes — not pushed
  // to the bottom of the chat area.
  toolProgress?: ToolProgressData | null
}

const Messages = ({
  conversationId,
  conversationLoading,
  messages,
  handleFeedbackSubmitted,
  onEditClick,
  isEditDisabled,
  editingMessageId,
  isEditSaving = false,
  messagesAfterCount = 0,
  onEditConfirm,
  onEditCancel,
  onRegenerate,
  isRegenerateDisabled,
  getVersionInfo,
  getUserVersionInfo,
  onNavigateVersion,
  toolProgress,
}: MessagesProps) => {
  const scrollRef = useRef<HTMLDivElement>(null)

  // Stable React keys for messages. When the backend syncs a DB id onto a
  // message the id changes, but the DOM element should NOT remount (which would
  // re-trigger animations / scroll jumps). We track the first id seen at each
  // array index and keep using it. Reset on conversation switch so bubbles
  // remount properly.
  const stableKeysRef = useRef<string[]>([])
  const lastConversationIdRef = useRef<string | null>(null)
  const stableKeys = useMemo(() => {
    if (conversationId !== lastConversationIdRef.current) {
      stableKeysRef.current = []
      lastConversationIdRef.current = conversationId
    }
    const prev = stableKeysRef.current
    const keys = messages.map((m, i) => {
      // For positions we've seen before, keep the original key
      // (handles backend ID sync where id changes but position stays).
      if (i < prev.length) return prev[i]
      return m.id
    })
    stableKeysRef.current = keys
    return keys
  }, [messages, conversationId])

  const latestMessage = messages[messages.length - 1]
  const latestMessageIndex = messages.length - 1
  const latestMessageRole = latestMessage?.role ?? null

  // Sticky-bottom: keep the scroll pinned to the latest content as it streams,
  // but only if the user is already near the bottom. If they've scrolled up to
  // read earlier turns, we leave their scroll position alone. The handler only
  // mutates a ref, so scrolling does not re-render the list.
  const stickToBottomRef = useRef(true)
  const lastAutoStickResetRef = useRef<{
    conversationId: string | null
    userMessageIndex: number | null
  }>({ conversationId: null, userMessageIndex: null })

  useLayoutEffect(() => {
    const prev = lastAutoStickResetRef.current
    const conversationChanged = conversationId !== prev.conversationId
    const newUserTurn =
      latestMessageRole === 'user' &&
      latestMessageIndex >= 0 &&
      latestMessageIndex !== prev.userMessageIndex

    if (conversationChanged || newUserTurn) {
      stickToBottomRef.current = true
    }

    lastAutoStickResetRef.current = {
      conversationId,
      userMessageIndex:
        latestMessageRole === 'user'
          ? latestMessageIndex
          : conversationChanged
            ? null
            : prev.userMessageIndex,
    }
  }, [conversationId, latestMessageIndex, latestMessageRole])

  useEffect(() => {
    const container = scrollRef.current
    if (!container) return
    const handleScroll = () => {
      const distanceFromBottom =
        container.scrollHeight - container.scrollTop - container.clientHeight
      stickToBottomRef.current = distanceFromBottom < 80
    }
    container.addEventListener('scroll', handleScroll, { passive: true })
    return () => container.removeEventListener('scroll', handleScroll)
  }, [])

  // Scroll before paint during streaming; otherwise each token can briefly
  // render above the final position and read as flicker.
  useLayoutEffect(() => {
    if (!stickToBottomRef.current) return
    const container = scrollRef.current
    if (!container) return
    container.scrollTop = container.scrollHeight
  }, [messages, conversationLoading])

  // Build a lookup: message index → preceding user question (O(N) single pass)
  const userQuestionByIndex = useMemo(() => {
    const map = new Map<number, string | undefined>()
    let lastUserContent: string | undefined
    for (let i = 0; i < messages.length; i++) {
      if (messages[i].role === 'user') {
        lastUserContent = messages[i].content
      } else {
        map.set(i, lastUserContent)
      }
    }
    return map
  }, [messages])

  // W16 / TIME-198: index of the actively-streaming assistant bubble.
  // Only that bubble gets the `toolProgress` prop — all others get
  // null and skip the render branch.
  //
  // The greedy "last non-user message" heuristic landed on the
  // previous turn's assistant bubble when a tool_progress frame
  // arrived before the new turn's first assistant chunk (the spec
  // says progress fires WHILE the tool blocks the streamText loop,
  // i.e. before any assistant content). To avoid that wrong-bubble
  // race we only return an index when the message list actually ends
  // with an assistant bubble; otherwise we return -1 and the parent
  // falls back to rendering the line under `LoadingMessage` in the
  // scroll container.
  const streamingAssistantIndex = useMemo(() => {
    if (!conversationLoading || messages.length === 0) return -1
    const last = messages[messages.length - 1]
    if (last.role === 'user') return -1
    return messages.length - 1
  }, [messages, conversationLoading])

  return (
    <div className="flex-1 min-h-0 flex flex-col">
      {/* Scrollable: all messages rendered inline in send order. */}
      <div
        ref={scrollRef}
        className="flex-1 min-h-0 overflow-y-auto px-3 py-2 space-y-3"
      >
        {conversationId &&
          messages.map((message, index) => {
            // Persona shown on an assistant reply = the agent the user
            // @mentioned in the prompting message. The backend persists only
            // the generic plant_support_engineer, so derive it here so the
            // avatar + label stay correct across streaming, history reload,
            // and version views.
            const mentionedAgent =
              message.role !== 'user'
                ? getMentionedAgent(userQuestionByIndex.get(index) ?? '')
                : null
            const displayMessage = mentionedAgent
              ? { ...message, agent_used: mentionedAgent }
              : message
            return (
            <div
              key={stableKeys[index] ?? message.id}
            >
              <MessageBubble
                message={displayMessage}
                handleFeedbackSubmitted={handleFeedbackSubmitted}
                onEditClick={onEditClick}
                isEditDisabled={isEditDisabled}
                isEditing={message.id === editingMessageId}
                isEditSaving={message.id === editingMessageId && isEditSaving}
                messagesAfterCount={message.id === editingMessageId ? messagesAfterCount : 0}
                onEditConfirm={onEditConfirm}
                onEditCancel={onEditCancel}
                onRegenerate={onRegenerate}
                isRegenerateDisabled={isRegenerateDisabled}
                versionInfo={getVersionInfo?.(message) ?? getUserVersionInfo?.(message) ?? null}
                onNavigateVersion={onNavigateVersion}
                userQuestion={message.role !== 'user' ? userQuestionByIndex.get(index) : undefined}
                toolProgress={index === streamingAssistantIndex ? toolProgress : null}
              />
            </div>
            )
          })}
        {conversationLoading && (() => {
          const lastMsg = messages[messages.length - 1]
          const hasActiveAssistantStream = lastMsg?.role === 'assistant' && (lastMsg.reasoning_path || lastMsg.content)
          return !hasActiveAssistantStream ? <LoadingMessage /> : null
        })()}
        {/* W16 / TIME-198 fallback: when no assistant bubble exists yet
          *  (the gap between user-send and first-assistant-chunk),
          *  `streamingAssistantIndex` is -1 and no MessageBubble can
          *  carry the line. Render it inside the scroll container next
          *  to LoadingMessage so the user still sees the slow tool's
          *  progress during that window. Once an assistant bubble
          *  arrives, the per-bubble path takes over. */}
        {conversationLoading && streamingAssistantIndex === -1 && toolProgress && (
          <div className="flex justify-start" data-testid="tool-progress-status-fallback">
            <ToolProgressStatus data={toolProgress} />
          </div>
        )}
      </div>
    </div>
  )
}

export default Messages
